import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {TranslationService} from '../../../core/services/translation.service';
import {NavigationCommand, NavigationService} from '../../../data/services/navigation.service';
import {HupSubscriptionsService} from '../../../data/services/hup-subscriptions.service';
import {HupSubscription} from '../../../data/models/subscription.model';

/**
 * Bestätigungsseite nach erfolgreicher Zubuchung (P5W-4471).
 *
 * Route: /additional-booking/ordered/:orderId - `orderId` ist die BackendId des NEU angelegten
 * Kombi-Abos (nicht die des Hauptabonnements).
 *
 * Das Abo wird hier bewusst über die Route nachgeladen statt im Navigationszustand mitgegeben:
 * so übersteht die Seite ein Neuladen, und `getSubscription` liefert das Abo durch
 * `handleSubscription` angereichert (Produktbezeichnung, Ausgabe, Auftragsnummer, Zeitraum).
 * Dasselbe Muster nutzt die Bestellbestätigung OfferOrderedOverviewComponent.
 */
@Component({
  selector: 'app-additional-booking-ordered',
  templateUrl: './additional-booking-ordered.component.html',
  styleUrls: ['./additional-booking-ordered.component.scss']
})
export class AdditionalBookingOrderedComponent implements OnInit, OnDestroy {

  /**
   * Kombi-Abos werden direkt in Paradise angelegt und liegen nie in der Web-DB; die webId ist
   * daher immer 0. pca fällt bei fehlendem Web-DB-Treffer auf Paradise zurück.
   */
  private static readonly WEB_ID_PARADISE_ONLY = '0';

  protected order: HupSubscription = null;
  protected errors: string[] = [];
  protected isLoading: boolean = true;

  private paramSub: Subscription;

  constructor(public translationService: TranslationService,
              private route: ActivatedRoute,
              private navigationService: NavigationService,
              private subscriptionService: HupSubscriptionsService,
              @Inject(PLATFORM_ID) private platformId: Object) {
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Beim Server-Rendering liegt kein SSO-Token vor; das Abo wäre nicht ladbar.
      return;
    }
    this.paramSub = this.route.paramMap.subscribe(params => {
      this.loadSubscription(params.get('orderId'));
    });
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
  }

  private loadSubscription(orderId: string): void {
    this.isLoading = true;
    this.order = null;
    this.errors = [];

    this.subscriptionService
      .getSubscription(AdditionalBookingOrderedComponent.WEB_ID_PARADISE_ONLY, orderId)
      .subscribe({
        next: subscription => {
          this.order = subscription;
          this.isLoading = false;
        },
        error: error => {
          console.error(`Zugebuchtes Abonnement ${orderId} konnte nicht geladen werden`, error);
          this.errors.push(this.translationService.getTranslation('error_subscription_init'));
          this.isLoading = false;
        }
      });
  }

  /** Zurück zur Zubucher-Übersicht, um ein weiteres Abonnement zu erweitern. */
  public continueBooking(): void {
    this.navigationService.navigateTo(NavigationCommand.ADDITIONALBOOKINGOVERVIEW);
  }

  /** Führt in die Abonnement-Übersicht des Kunden, in der das neue Abo nun mit aufgeführt ist. */
  public goToSubscriptions(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION);
  }
}
